import { TDocumentDefinitions } from "pdfmake/interfaces";

const formatDateTime = (iso?: string) => {
  if (!iso) return "-";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatCurrency = (v: number | string = 0) =>
  typeof v === "number"
    ? v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Number(String(v).replace(/,/g, "") || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      });

const FormPackageReceipt = (props: any): TDocumentDefinitions => {
  const companyLeft = [
    { text: "Venture Catalyst Co.,Ltd (Head Office)", bold: true, fontSize: 16 },
    { text: "77/104 Sinn Sathorn Tower Building, 25th Floor." },
    { text: "Krungthonburi Road., Klongtonsai, Klongsan Bangkok 10600" },
    { text: "TAX ID:  0 1055 52096 43 5" },
  ];

  const receiptNo = props.receipt_no || "VCM251000001";
  const receiptDate = props.date || formatDateTime("2025-10-30");

  const items =
    Array.isArray(props.items) && props.items.length
      ? props.items
      : [
          { no: 1, description: "Package A", qty: 1, unit_price: 1000.0 },
          { no: 2, description: "Package B", qty: 2, unit_price: 2000.0 },
        ];

  const total_price_num =
    props.total_price_num ??
    items.reduce((s: number, it: any) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const vat = props.vat ?? Math.round(total_price_num * 0.07 * 100) / 100;
  const total_incl_vat = props.total_incl_vat ?? total_price_num + vat;
  const total_price_text = props.total_price_text || "แปดพันเจ็ดร้อยบาทถ้วน";

  const itemRows = items.map((it: any) => [
    { text: String(it.no), alignment: "center" },
    { text: it.description || "-", alignment: "left" },
    { text: it.qty, alignment: "center" },
    { text: formatCurrency(it.unit_price), alignment: "right" },
    { text: formatCurrency(it.price), alignment: "right" },
  ]);

  const tableBody: any[] = [];

  tableBody.push([
    { text: "ลำดับ", style: "tableHeader", alignment: "center" },
    { text: "รายการ", style: "tableHeader", alignment: "center" },
    { text: "จำนวน\n(หน่วย)", style: "tableHeader", alignment: "center" },
    { text: "ราคาต่อหน่วย\n(บาท)", style: "tableHeader", alignment: "center" },
    { text: "จำนวนเงิน\n(บาท)", style: "tableHeader", alignment: "center" },
  ]);

  itemRows.forEach((r: any) => tableBody.push(r));

  tableBody.push([
    {},
    {
      text: `*** Order No : ${receiptNo || "-"} Date : ${props.order_created_at || "-"}`,
      alignment: "left",
      fontSize: 10,
      marginBottom: 50,
    },
    {},
    {},
    {},
  ]);

  tableBody.push([
    {
      text: "บาท",
      alignment: "center",
      bold: true,
    },
    { text: total_price_text, alignment: "center", colSpan: 2, bold: true },
    {},
    { text: "ยอดรวม", style: "tableHeader", alignment: "right" },
    { text: total_price_num, alignment: "right", bold: true },
  ]);

  tableBody.push([
    { text: "", colSpan: 3, border: [false, false, false, false] },
    {},
    {},
    { text: "ภาษีมูลค่าเพิ่ม", alignment: "right", bold: true },
    { text: vat, alignment: "right", bold: true },
  ]);

  tableBody.push([
    { text: "", colSpan: 3, border: [false, false, false, false] },
    {},
    {},
    { text: "ยอดรวมทั้งสิ้น", alignment: "right", bold: true },
    { text: total_incl_vat, alignment: "right", bold: true },
  ]);

  const doc: TDocumentDefinitions = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [40, 40, 40, 0],
    info: { title: props.file_name || `Receipt_${receiptNo}` },
    content: [
      {
        columns: [
          {
            width: "55%",
            stack: companyLeft.map((t) => ({
              ...t,
              text: t.text || t,
              margin: [0, 2, 0, 2],
            })),
          },
          {
            width: "45%",
            alignment: "right",
            stack: [
              { text: "ใบเสร็จรับเงิน/ใบกำกับภาษี", bold: true, fontSize: 16 },
              { text: "Receipt / Tax Invoice", bold: true, fontSize: 16 },
              { text: "(สำเนา)", bold: true, fontSize: 16, margin: [0, 2, 0, 6] },
              { text: `เลขที่ : ${receiptNo}`, bold: true },
              { text: `วันที่ : ${receiptDate}`, bold: true },
            ],
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 12],
      },

      {
        columns: [
          {
            width: 60,
            text: "ที่อยู่ผู้สั่งซื้อ :",
            bold: true,
            alignment: "left",
            margin: [0, 0, 4, 0],
          },
          {
            width: "*",
            stack: [
              ...(props.buyer_address_lines?.length
                ? props.buyer_address_lines.map((line: any) => ({
                    text: line,
                    margin: [0, 0, 0, 2],
                  }))
                : [{ text: "-", margin: [0, 0, 0, 2] }]),

              {
                text: `เลขประจำตัวผู้เสียภาษี : ${props.buyer_tax_id || "x xxxx xxxxx xx x"}`,
                margin: [0, 2, 0, 0],
              },
            ],
          },
        ],
        columnGap: 4,
        margin: [0, 0, 0, 12],
      },
      {
        table: {
          headerRows: 1,
          widths: [40, "*", 40, 80, 100],
          body: tableBody,
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            if (i === 0) return 1;
            if (i === 1) return 1;
            if (i === node.table.body.length - 3) return 1;
            if (i === node.table.body.length - 2) return 1;
            if (i === node.table.body.length - 1) return 1;
            if (i === node.table.body.length) return 1;
            return 0;
          },
          vLineWidth: () => 0.5,
          hLineColor: () => "#aaaaaa",
          vLineColor: () => "#aaaaaa",
        },
        margin: [0, 0, 0, 12],
      },

      {
        columns: [
          {
            width: "50%",
            stack: [
              { text: "ชำระโดย", bold: true, margin: [0, 0, 0, 6] },
              {
                table: {
                  widths: ["*"],
                  body: [
                    [
                      {
                        stack: [
                          {
                            columns: [
                              {
                                width: 20,
                                stack: [
                                  {
                                    canvas: [
                                      { type: "rect", x: 0, y: 0, w: 12, h: 12, lineWidth: 1 },
                                    ],
                                  },
                                ],
                                alignment: "center",
                              },
                              { width: "*", text: "เงินสด" },
                            ],
                            margin: [0, 2, 0, 2],
                          },
                          {
                            columns: [
                              {
                                width: 20,
                                stack: [
                                  {
                                    canvas: [
                                      { type: "rect", x: 0, y: 0, w: 12, h: 12, lineWidth: 1 },
                                    ],
                                  },
                                ],
                                alignment: "center",
                              },
                              {
                                width: "*",
                                stack: [
                                  { text: "โอนเงินผ่านธนาคาร", margin: [0, 0, 0, 2] },
                                  {
                                    text: "ธนาคารกสิกรไทย ประเภทออมทรัพย์",
                                  },
                                  {
                                    text: "ชื่อบัญชี : บริษัท เวนเจอร์ คะตาลิสต์ จำกัด",
                                  },
                                  {
                                    text: "เลขที่บัญชี : 068-2-05448-6",
                                  },
                                ],
                              },
                            ],
                            margin: [0, 2, 0, 2],
                          },
                        ],
                        margin: [2, 2, 2, 2],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0.5,
                  hLineColor: () => "#000000",
                  vLineColor: () => "#000000",
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 2,
                  paddingBottom: () => 2,
                },
              },
            ],
          },
          {
            width: "50%",
            alignment: "right",
            stack: [
              {
                text: "Venture Catalyst Co.,Ltd",
                margin: [0, 50, 0, 20],
                alignment: "center",
              },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 5,
                    x2: 200,
                    y2: 5,
                    lineWidth: 0.2,
                  },
                ],
                alignment: "center",
              },
              {
                text: "ผู้รับเงิน / ผู้มีอำนาจลงนาม",
                margin: [0, 10, 0, 0],
                alignment: "center",
              },
            ],
          },
        ],
        columnGap: 10,
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 12 },
    styles: { tableHeader: { bold: true, fontSize: 12, alignment: "center" } },
  };

  return doc;
};

export default FormPackageReceipt;
